import { deepClone } from '@src/utility/deepClone';
import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('utility:extraElementData');

type ExtraElementDataRoot = Record<string, unknown>;

type VideoLike = { extraElementData?: unknown } | null | undefined;

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function ensureRoot(video: VideoLike): ExtraElementDataRoot | null {
  if (!video) return null;
  if (!isRecord(video.extraElementData)) {
    // initialize empty root
    Object.defineProperty(video, 'extraElementData', {
      value: {},
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  const rootUnknown = video.extraElementData;
  return isRecord(rootUnknown) ? rootUnknown : null;
}

/** Upsert (bucket[elementId] = value) while preserving other buckets */
export function upsertIdMapping(
  video: VideoLike,
  bucket: string,
  elementId: string,
  value: string,
): void {
  const root = ensureRoot(video);
  if (!root) return;
  const existingBucket = root[bucket];
  let map: Record<string, string> = {};
  if (isRecord(existingBucket)) {
    for (const [k, v] of Object.entries(existingBucket)) {
      if (typeof v === 'string') map[k] = v;
    }
  } else if (existingBucket !== undefined) {
    logger.warn('Overwriting non-object bucket in extraElementData', {
      bucket,
      kind: typeof existingBucket,
    });
  }
  map[elementId] = value;
  root[bucket] = map;
}

export function getIdMapping(
  video: VideoLike,
  bucket: string,
  elementId: string,
): string | undefined {
  const root = ensureRoot(video);
  if (!root) return undefined;
  const b = root[bucket];
  if (!isRecord(b)) return undefined;
  const raw = b[elementId];
  return typeof raw === 'string' ? raw : undefined;
}

export function removeIdMapping(
  video: VideoLike,
  bucket: string,
  elementId: string,
): void {
  const root = ensureRoot(video);
  if (!root) return;
  const b = root[bucket];
  if (!isRecord(b)) return;
  if (elementId in b) delete b[elementId];
}

/** Generic per-element object upsert (shallow merge). Creates object if missing. */
export function upsertElementMetaObject<T extends object>(
  video: VideoLike,
  elementId: string,
  patch: Partial<T>,
): void {
  const root = ensureRoot(video);
  if (!root) return;
  const existing = root[elementId];
  if (!isRecord(existing)) {
    root[elementId] = { ...patch };
    return;
  }
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    existing[k] = v;
  }
}

export function deleteElementMeta(video: VideoLike, elementId: string): void {
  const root = ensureRoot(video);
  if (!root) return;
  if (elementId in root) delete root[elementId];
}

/** Retrieve a deep copy of an element's meta object (if present). */
export function getElementMetaObject(
  video: VideoLike,
  elementId: string,
): Record<string, unknown> | undefined {
  const root = ensureRoot(video);
  if (!root) return undefined;
  const existing = root[elementId];
  if (!isRecord(existing)) return undefined;
  // Return a deep copy so callers can freely mutate without affecting source
  const cloned = deepClone(existing);
  if (isRecord(cloned)) return cloned;
  return undefined;
}
